import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import type { Client } from "../../../../shared/domains/client/client";
import { useClientDisplayName } from "../../../../shared/domains/client/client";
import { Path } from "../../../core/routing/path";
import { theme } from "../../styles/theme";
import { GlassAvatar } from "../glass-avatar";
import { Circle } from "../shape/circle";
import { LogoutIcon } from "../svg/logout-icon";
import { sideBarColors } from "./component/colors";

interface BottomNavProps {
  client?: Client | null;
  onDisconnectClick: () => void;
  onProfileClick: () => void;
}

export function BottomNav(props: BottomNavProps) {
  const { onDisconnectClick, onProfileClick, client } = props;

  const displayName = useClientDisplayName(client, true);
  const initials = [client?.firstName, client?.lastName]
    .map((part) => part?.trim()?.[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();

  return (
    <BottomContainer>
      <Separator />
      <BottomWrapper>
        <ProfileContainer>
          {!!client && (
            <>
              <UserButton to={Path.Profile} onClick={onProfileClick}>
                <GlassAvatar initials={initials} size={48} />
              </UserButton>
              <ProfileInfos>
                {displayName && <Name>{displayName}</Name>}
                {client?.email ? <Email>{client.email}</Email> : null}
              </ProfileInfos>
            </>
          )}
        </ProfileContainer>
        <DisconnectCircle size={35} onClick={onDisconnectClick}>
          <DisconnectButton size={25}>
            <LogoutIcon />
          </DisconnectButton>
        </DisconnectCircle>
      </BottomWrapper>
    </BottomContainer>
  );
}

const Email = styled.span`
  margin-top: 2px;
  font-size: 0.75rem;
  color: ${sideBarColors.profile};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BottomContainer = styled.div`
  position: relative;
  bottom: 0;
  background-color: ${sideBarColors.bottomBackground};
  border-radius: 0 0 11px 11px;
  display: flex;
  flex-direction: column;
  flex: 0;
  height: auto;
  align-items: stretch;
`;

const Separator = styled.div`
  margin: 0 16px;
  width: auto;
  height: 1px;
  align-self: center;
  background-color: ${sideBarColors.bottomSeparator};
`;

const BottomWrapper = styled.div`
  background-color: ${sideBarColors.bottomBackground};
  border-radius: 0 0 11px 11px;
  display: flex;
  flex-direction: row;
  flex: 1;
  height: auto;
  padding: 12px;
  align-items: center;
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const UserButton = styled(Link)`
  &:hover {
    cursor: pointer;
  }
`;

const DisconnectCircle = styled(Circle)`
  border: solid 1px ${sideBarColors.disconnectCircle};
  margin: 10px;
  &:hover {
    cursor: pointer;
    border: solid 2px ${sideBarColors.disconnectCircle};
  }
`;

const DisconnectButton = styled(Circle)`
  background-color: ${theme.mainColor};
  &:hover {
    opacity: 0.8;
  }
`;

const ProfileInfos = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 8px;
  max-width: 140px;
  text-align: left;
`;

const Name = styled.span`
  ${theme.mediumText}
  font-size: 0.9375rem;
  color: ${sideBarColors.name};
  text-overflow: ellipsis;
  overflow: hidden;
`;
